import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as FlightSvg } from "../../assets/flight.svg";
import { ReactComponent as GroupIcon } from "../../assets/group.svg";
import { dispatchAction } from "../../services/alertService";
import { ENDPOINTS } from "../../utils/appConfig";
import {
  ShieldIcon,
  TablerAlertTriangle,
  TablerDrone,
  TablerEye,
  TablerHigh,
  TablerLow,
  TablerMedium,
} from "../shared/Icons";
import "./ActiveAlertCard.css";

// ─── Action Config Table ────────────────────────────────────────────────────
// Maps every known LLM recommended_action string to its UI config.
// "dispatchedLabel" is the string LLM sends back when the action is done.
const ACTION_CONFIG = {
  "Dispatch Drone": {
    dispatchedLabel: "Drone Dispatched",
    labelKey: "dispatch.label_dispatch_drone",
    doneLabelKey: "dispatch.done_drone",
    icon: (size) => <TablerDrone size={size} />,
    style: "primary",
  },
  "Dispatch Small Team": {
    dispatchedLabel: "Small Team Dispatched",
    labelKey: "dispatch.label_dispatch_small_team",
    doneLabelKey: "dispatch.done_small_team",
    icon: (size) => <GroupIcon width={size} height={size} stroke="currentColor" />,
    style: "outline",
  },
  "Dispatch Police": {
    dispatchedLabel: "Police Dispatched",
    labelKey: "dispatch.label_dispatch_police",
    doneLabelKey: "dispatch.done_police",
    icon: (size) => <ShieldIcon size={size} />,
    style: "outline",
  },
  "Dispatch Ambulance Service": {
    dispatchedLabel: "Ambulance Dispatched",
    labelKey: "dispatch.label_dispatch_ambulance_service",
    doneLabelKey: "dispatch.done_ambulance_service",
    icon: (size) => <TablerAlertTriangle size={size} />,
    style: "outline",
  },
  "Dispatch Fire Service": {
    dispatchedLabel: "Fire Service Dispatched",
    labelKey: "dispatch.label_dispatch_fire_service",
    doneLabelKey: "dispatch.done_fire_service",
    icon: (size) => <FlightSvg width={size} height={size} stroke="currentColor" />,
    style: "outline",
  },
  "Dispatch Maintenance Team": {
    dispatchedLabel: "Maintenance Team Dispatched",
    labelKey: "dispatch.label_dispatch_maintenance_team",
    doneLabelKey: "dispatch.done_maintenance_team",
    icon: (size) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    ),
    style: "outline",
  },
};

// All "dispatched" values from the config — used to detect already-dispatched states
const ALL_DISPATCHED_VALUES = new Set(
  Object.values(ACTION_CONFIG).map((c) => c.dispatchedLabel)
);

// Reverse map: "Drone Dispatched" -> "Dispatch Drone"
const DISPATCHED_TO_ACTION = Object.fromEntries(
  Object.entries(ACTION_CONFIG).map(([action, config]) => [config.dispatchedLabel, action])
);

// ─── Component ───────────────────────────────────────────────────────────────
function ActiveAlertCard({ alert, formatDateTime, isDroneDispatched, setSearchParams }) {
  const { t } = useTranslation();
  const [streamFailed, setStreamFailed] = React.useState(false);
  // Track locally dispatched actions so the UI updates instantly without needing a refetch
  const [localDispatched, setLocalDispatched] = React.useState(new Set());

  const primaryDevice =
    (alert.devices || []).find(
      (d) => d.primary_device === true || d.primary_device === "true"
    ) || (alert.devices && alert.devices.length ? alert.devices[0] : null);

  const streamUrl = primaryDevice?.camera?.id
    ? ENDPOINTS.CAMERA_STREAM(primaryDevice.camera.id)
    : null;

  React.useEffect(() => {
    setStreamFailed(false);
  }, [streamUrl]);

  // ── Normalise recommended_action from llm_response ──────────────────────
  const recommendedActions = React.useMemo(() => {
    const raw = alert.llm_response?.recommended_action;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [alert.llm_response]);

  // ── Derive which "Dispatch X" and which "*Dispatched" items are in the list
  const dispatchActions = React.useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const a of recommendedActions) {
      if (ACTION_CONFIG[a] && !seen.has(a)) {
        seen.add(a);
        result.push(a);
        continue;
      }
      const original = DISPATCHED_TO_ACTION[a];
      if (original && !seen.has(original)) {
        seen.add(original);
        result.push(original);
      }
    }
    return result;
  }, [recommendedActions]);

  // Set of "dispatched label" values that appear in recommendedActions
  // e.g. "Drone Dispatched" means the drone was already dispatched
  const alreadyDispatchedFromLLM = React.useMemo(
    () => new Set(recommendedActions.filter((a) => ALL_DISPATCHED_VALUES.has(a))),
    [recommendedActions]
  );

  // Build final dispatched set: union of LLM state + local optimistic state
  const dispatchedSet = React.useMemo(() => {
    const combined = new Set([...alreadyDispatchedFromLLM, ...localDispatched]);
    // Also honour the legacy isDroneDispatched prop
    if (
      isDroneDispatched ||
      alert.alert_analysis?.toLowerCase() === "complete" ||
      alert.alert_analysis?.toLowerCase() === "completed"
    ) {
      combined.add("Drone Dispatched");
    }
    return combined;
  }, [alreadyDispatchedFromLLM, localDispatched, isDroneDispatched, alert.alert_analysis]);

  const isAlertComplete = React.useMemo(() => {
    const s = (alert.alert_analysis || "").toLowerCase();
    return s === "complete" || s === "completed";
  }, [alert.alert_analysis]);

  const isAlertFailed = React.useMemo(() => {
    return (alert.alert_analysis || "").toLowerCase() === "failed";
  }, [alert.alert_analysis]);

  // ── Dispatch handler (self-contained, uses full payload the API expects) ──
  const handleDispatch = React.useCallback(
    async (action) => {
      const config = ACTION_CONFIG[action];
      if (!config) return;
      // Optimistically mark as dispatched
      setLocalDispatched((prev) => new Set([...prev, config.dispatchedLabel]));
      try {
        const primaryDevice = (alert.devices || []).find((d) => d.primary_device);
        const latitude = primaryDevice?.camera?.latitude;
        const longitude = primaryDevice?.camera?.longitude;
        const payload = {
          action,
          alert: alert.id,
          context: {
            latitude,
            longitude,
            notes: "Suspicious activity detected",
          },
        };
        await dispatchAction(payload);
        toast.success(t("label.dispatchSuccess", { label: t(config.labelKey) }));
      } catch (err) {
        console.error("Dispatch error:", err);
        toast.error(t("label.dispatchFailed", { label: t(config.labelKey) }));
        // Rollback optimistic update on failure
        setLocalDispatched((prev) => {
          const next = new Set(prev);
          next.delete(config.dispatchedLabel);
          return next;
        });
      }
    },
    [alert, t]
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getSeverityIcon = (severity) => {
    if (!severity) return null;
    const s = severity.toLowerCase();
    if (s === "high") return <TablerHigh size={14} />;
    if (s === "medium") return <TablerMedium size={14} />;
    if (s === "low") return <TablerLow size={14} />;
    return null;
  };

  const formatBadgeLabel = (value) => {
    if (!value) return "N/A";
    return String(value)
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="active-alert-card">
      {/* Left: Video stream preview */}
      <div className="active-alert-video-panel">
        {streamUrl && !streamFailed ? (
          <img
            src={streamUrl}
            alt=""
            className="active-alert-video-frame"
            onError={() => setStreamFailed(true)}
          />
        ) : (
          <div className="active-alert-video-placeholder error-state">
            <svg className="placeholder-icon" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34" />
              <path d="M23 7l-7 5 7 5V7z" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <span>{t("label.streamingStopped")}</span>
          </div>
        )}
        {streamUrl && !streamFailed && (
          <div className="video-overlay-live">
            <span className="live-dot-pulsing"></span>LIVE
          </div>
        )}
      </div>

      {/* Middle: Content details & action buttons */}
      <div className="active-alert-info-panel">
        <h2 className="active-alert-title">{alert.message || "Person detected in monitored area"}</h2>
        <div className="active-alert-device-info">
          {primaryDevice?.camera_name || primaryDevice?.camera?.name || "Unknown Camera"} |{" "}
          {formatDateTime(alert.start_time)}
        </div>

        {/* Dynamic action buttons from LLM recommended_action — hidden for failed alerts */}
        {!isAlertFailed && (
        <div className="active-alert-actions-row">
          {dispatchActions.map((action) => {
            const config = ACTION_CONFIG[action];
            const isDispatched = dispatchedSet.has(config.dispatchedLabel);
            return (
              <button
                key={action}
                className={`action-btn-dispatch ${isDispatched ? "dispatched" : config.style === "primary" ? "primary" : "outline"}`}
                disabled={isDispatched || isAlertComplete}
                onClick={() => handleDispatch(action)}
              >
                {isDispatched ? (
                  t(config.doneLabelKey)
                ) : (
                  <>
                    <span className="action-btn-icon">{config.icon(13)}</span>
                    {t(config.labelKey)}
                  </>
                )}
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* Right: Severity Badge & View Detail Button */}
      <div className="active-alert-right-panel">
        <span
          className={`alerts-badge ${
            alert.severity?.toLowerCase() === "high"
              ? "badge-severity-high"
              : alert.severity?.toLowerCase() === "medium"
              ? "badge-severity-medium"
              : "badge-severity-low"
          }`}
        >
          {getSeverityIcon(alert.severity)}
          {formatBadgeLabel(alert.severity)}
        </span>
        <Link
          className="action-btn-view-detail"
          to={`/alert-details?alertId=${encodeURIComponent(alert.id)}&type=${alert.type || "camera"}`}
          onClick={(e) => {
            if (setSearchParams) {
              e.preventDefault();
              setSearchParams({ alertId: alert.id, type: alert.type || "camera" });
            }
          }}
        >
          <TablerEye size={16} style={{ marginRight: "6px" }} />
          {t("label.viewDetail")}
        </Link>
      </div>
    </div>
  );
}

export default ActiveAlertCard;